import { get } from "lodash";
import {
  Box,
  Flex,
  Icon,
  Loading,
  Select,
  Small,
  TextInput,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  auth0UserMetadataKey,
  DIRECTORY_ROLE,
  POWER_ROLE,
  ADMIN_ROLE,
} from "src/services/auth";
import isValidEmail from "src/util/email";

const IMPERSONATE_ROLE_PLACEHOLDER = (
  <Icon icon={["fas", "user-secret"]} color="greyDarkest" />
);

const IMPERSONATION_ROLE_OPTIONS = [
  { value: DIRECTORY_ROLE, label: "Directory" },
  { value: POWER_ROLE, label: "Power" },
  { value: ADMIN_ROLE, label: "Admin" },
];

const EMAIL_INPUT_COLLAPSED_PX = 48;
const EMAIL_INPUT_EXPANDED_PX = 228;

/* Targets ORCS TextInput iconLeft markup; breaks if TextInput wrapper changes — fix opacity in orcs-design-system if needed. */
const ImpersonationEmailShell = styled.div`
  display: inline-block;
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  transition: width 0.18s ease-out;
  width: ${(p) =>
    p.$expanded
      ? `${EMAIL_INPUT_EXPANDED_PX}px`
      : `${EMAIL_INPUT_COLLAPSED_PX}px`};
  /* TextInput iconLeft wrapper sets svg opacity 0.4; match Select placeholder icon */
  & label[for="support-impersonation-email"] svg {
    opacity: 1;
  }
`;

const SupportImpersonationControls = ({ user, setSupportImpersonation }) => {
  const metadataImpersonationRoles = get(
    user,
    [auth0UserMetadataKey, "impersonation", "roles"],
    []
  );
  const currentImpersonationRole = Array.isArray(metadataImpersonationRoles)
    ? metadataImpersonationRoles[0] || null
    : null;
  const metadataImpersonationEmail =
    get(user, [auth0UserMetadataKey, "impersonation", "email"], null) ?? "";
  const committedEmail = metadataImpersonationEmail.trim();

  const [impersonationRole, setImpersonationRole] = useState(
    currentImpersonationRole
  );
  const [impersonationEmail, setImpersonationEmail] = useState(
    metadataImpersonationEmail
  );
  const [impersonationError, setImpersonationError] = useState("");
  const [emailValidationError, setEmailValidationError] = useState(false);
  const [isImpersonationBusy, setIsImpersonationBusy] = useState(false);
  const [isEmailFieldFocused, setIsEmailFieldFocused] = useState(false);

  useEffect(() => {
    setImpersonationRole(currentImpersonationRole);
  }, [currentImpersonationRole]);

  useEffect(() => {
    setImpersonationEmail(metadataImpersonationEmail);
  }, [metadataImpersonationEmail]);

  const onImpersonationRoleChange = async (option) => {
    if (isImpersonationBusy) {
      return;
    }

    const role = option?.value || null;
    const roles = role ? [role] : [];
    const previousRole = impersonationRole;

    setImpersonationError("");
    setEmailValidationError(false);
    setImpersonationRole(role);
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { roles } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationRole(previousRole);
      setImpersonationError(
        "Unable to update support impersonation right now. Please try again."
      );
      setIsImpersonationBusy(false);
    }
  };

  // trimmed: callers only pass after validate; must match state used for !== committed check
  const submitEmail = async (trimmed) => {
    if (isImpersonationBusy) {
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailValidationError(true);
      return;
    }
    setEmailValidationError(false);
    setImpersonationError("");
    setImpersonationEmail(trimmed);
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { email: trimmed } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationEmail(committedEmail);
      setImpersonationError(
        "Unable to update support impersonation right now. Please try again."
      );
      setIsImpersonationBusy(false);
    }
  };

  const clearCommittedEmail = async () => {
    if (isImpersonationBusy) {
      return;
    }
    setEmailValidationError(false);
    setImpersonationError("");
    setIsImpersonationBusy(true);

    try {
      await setSupportImpersonation({
        variables: { input: { email: "" } },
      });
      window.location.reload();
    } catch (err) {
      setImpersonationEmail(metadataImpersonationEmail);
      setImpersonationError(
        "Unable to update support impersonation right now. Please try again."
      );
      setIsImpersonationBusy(false);
    }
  };

  const onEmailKeyDown = (e) => {
    if (e.key !== "Enter") {
      return;
    }
    e.preventDefault();
    const trimmed = (impersonationEmail ?? "").trim();
    if (!trimmed && committedEmail) {
      setEmailValidationError(false);
      clearCommittedEmail();
      return;
    }
    if (!trimmed) {
      setEmailValidationError(false);
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailValidationError(true);
      return;
    }
    setEmailValidationError(false);
    if (trimmed !== committedEmail) {
      submitEmail(trimmed);
    }
  };

  const selectedOption =
    IMPERSONATION_ROLE_OPTIONS.find((opt) => opt.value === impersonationRole) ||
    null;

  const emailHasContent = (impersonationEmail ?? "").trim() !== "";
  const emailFieldExpanded =
    isEmailFieldFocused || emailHasContent || emailValidationError;

  return (
    <>
      <Box ml="s">
        <Select
          id="support-impersonation-role"
          hideLabel
          ariaLabel="Impersonate role"
          options={IMPERSONATION_ROLE_OPTIONS}
          value={selectedOption}
          onChange={onImpersonationRoleChange}
          isDisabled={isImpersonationBusy}
          isLoading={isImpersonationBusy}
          isClearable={Boolean(impersonationRole)}
          placeholder={IMPERSONATE_ROLE_PLACEHOLDER}
          height="30px"
          padding="0 8px"
        />
      </Box>
      <Flex ml="s" alignItems="center">
        <ImpersonationEmailShell $expanded={emailFieldExpanded}>
          <TextInput
            id="support-impersonation-email"
            data-testid="support-impersonation-email"
            type="email"
            placeholder=""
            iconLeft={["fas", "envelope"]}
            fullWidth
            value={impersonationEmail ?? ""}
            onChange={(e) => setImpersonationEmail(e.target.value)}
            onFocus={() => setIsEmailFieldFocused(true)}
            onBlur={() => setIsEmailFieldFocused(false)}
            onKeyDown={onEmailKeyDown}
            disabled={isImpersonationBusy}
            invalid={emailValidationError}
            height="30px"
            ariaLabel="Impersonate email"
          />
        </ImpersonationEmailShell>
        {isImpersonationBusy && <Loading inverted ml="xs" ariaLabel="Saving" />}
      </Flex>
      {impersonationError && (
        <Small ml="s" weight="bold">
          {impersonationError}
        </Small>
      )}
    </>
  );
};

SupportImpersonationControls.propTypes = {
  user: PropTypes.object.isRequired,
  setSupportImpersonation: PropTypes.func.isRequired,
};

export default SupportImpersonationControls;
